import { useFormikContext } from 'formik';
import { useEffect, useState, useRef } from 'react';

import poweredByGoogle from '@perpay-web/components/assets/images/poweredByGoogle.png';
import AddressInputListItem from '@perpay-web/components/base/AddressInput/AddressInputListItem';
import FormikCombobox from '@perpay-web/components/base/Combobox/FormikCombobox';
import FormikSelectInput from '@perpay-web/components/base/SelectInput/FormikSelectInput';
import FormikTextInput from '@perpay-web/components/base/TextInput/FormikTextInput';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import stateCodes from '@perpay-web/constants/stateCodes';
import { getClassName } from '@perpay-web/utils/domUtils';
import { EndToEndTestIdProvider } from '@perpay-web/utils/endToEndTestIds';
import { noop } from '@perpay-web/utils/noop';

import styles from './AddressInput.scss';
import { useAddressInputContext } from './useAddressInputContext';

const REQUIRED_ADDRESS_FIELDS = ['street_1', 'city', 'state', 'zipcode'];

export const addressHasErrors = (formErrors) =>
    !!REQUIRED_ADDRESS_FIELDS.find((field) => field in formErrors);

const addressWasTouched = (touched) =>
    !!REQUIRED_ADDRESS_FIELDS.find((field) => field in touched);

const showAddressErrors = (formErrors, touched) =>
    addressHasErrors(formErrors) && addressWasTouched(touched);

const AddressInput = ({
    onAddressLine1Change = noop,
    addressSuggestions = [],
    addressSuggestionsErrors = {},
    isLoadingAddressSuggestions,
    onSelectSuggestedAddress,
    onComboBoxFocused = noop,
    onComboBoxBlurred = noop,
    addressDetails = null,
    errors = {},
    className = '',
    addressInputText = 'Please type in your primary address. We use this address to ship your card and verify your identity.',
    inputLabel = 'Address (no P.O. boxes)',
    allowSuggestionOffset = true,
    stateCodeOptions = stateCodes,
}) => {
    const {
        errors: formErrors,
        touched,
        values,
        setFieldValue,
        validateForm,
    } = useFormikContext();
    const {
        showDetailedForm,
        setShowDetailedForm,
        showAddressSuggestions,
        setShowAddressSuggestions,
        showDetailedFormOnNoInput,
    } = useAddressInputContext();
    const addressSuggestionsContainsErrors =
        Object.keys(addressSuggestionsErrors).length > 0;

    const numListItems = addressSuggestions.length + 1;

    const [minDetailFormHeight, setMinDetailFormHeight] = useState(0);

    // Reference to suggestions list element.
    const suggestionsRef = useRef(null);

    // Reference to element being covered by suggestion list.
    const offsetRef = useRef(null);

    useEffect(() => {
        // Calculate how much to expand form by since suggestions list
        // can expand beyond the form container
        if (
            showAddressSuggestions &&
            suggestionsRef.current &&
            offsetRef.current &&
            allowSuggestionOffset
        ) {
            // Subtract the offset element height and list top margin.
            const offset =
                suggestionsRef.current.offsetTop +
                offsetRef.current.clientHeight;
            setMinDetailFormHeight(
                suggestionsRef.current.clientHeight - offset,
            );
        } else {
            setMinDetailFormHeight(0);
        }
    }, [
        suggestionsRef,
        offsetRef,
        showAddressSuggestions,
        addressSuggestions,
        allowSuggestionOffset,
    ]);

    useEffect(() => {
        if (addressDetails && addressDetails.street1) {
            setShowDetailedForm(true);
            setShowAddressSuggestions(false);
            const newValues = {
                street1: addressDetails.street1,
                street2: addressDetails.street2,
                city: addressDetails.city,
                state: addressDetails.state,
                zipcode: addressDetails.zipcode,
            };
            setFieldValue('street1', addressDetails.street1);
            setFieldValue('street2', addressDetails.street2);
            setFieldValue('city', addressDetails.city);
            setFieldValue('state', addressDetails.state);
            setFieldValue('zipcode', addressDetails.zipcode);
            validateForm(newValues);
        }
    }, [
        addressDetails,
        setFieldValue,
        setShowAddressSuggestions,
        setShowDetailedForm,
        validateForm,
    ]);

    useEffect(() => {
        if (
            !addressDetails &&
            !showDetailedForm &&
            (values.street2 || values.city || values.state || values.zipcode)
        ) {
            setShowDetailedForm(true);
        }
    }, [addressDetails, setShowDetailedForm, showDetailedForm, values]);

    useEffect(() => {
        if (
            (addressSuggestionsContainsErrors ||
                showAddressErrors(formErrors, touched)) &&
            showDetailedFormOnNoInput
        ) {
            setShowDetailedForm(true);
        }
    }, [
        formErrors,
        touched,
        addressSuggestionsContainsErrors,
        setShowDetailedForm,
        showDetailedFormOnNoInput,
        showDetailedForm,
    ]);

    return (
        <Stack className={getClassName(className, styles.content)}>
            <div className={styles['fields-row']}>
                <span>
                    <EndToEndTestIdProvider testId='address-line-1'>
                        <FormikCombobox
                            id='street1'
                            name='street1'
                            value={values.street1}
                            label={inputLabel}
                            autoComplete='street-address'
                            itemCount={addressSuggestions.length}
                            onSelect={(value) => {
                                onSelectSuggestedAddress({
                                    placeId: value,
                                });
                                setShowDetailedForm(true);
                                setShowAddressSuggestions(false);
                            }}
                            onFocus={onComboBoxFocused}
                            onBlur={onComboBoxBlurred}
                            onInputValueChange={(value) => {
                                if (value === '')
                                    setShowAddressSuggestions(false);
                                else setShowAddressSuggestions(true);
                                onAddressLine1Change(value);
                            }}
                            getValueAtIndex={(index) =>
                                addressSuggestions[index]
                                    ? addressSuggestions[index].line1
                                    : ''
                            }
                            loading={isLoadingAddressSuggestions}
                        >
                            {(renderProps) =>
                                showAddressSuggestions ? (
                                    <ul
                                        ref={suggestionsRef}
                                        className={styles.list}
                                    >
                                        {addressSuggestions.map(
                                            (item, index) => (
                                                <EndToEndTestIdProvider
                                                    testId={`address-combo-box-item-${index}`}
                                                    key={item.placeId}
                                                >
                                                    <AddressInputListItem
                                                        item={item}
                                                        renderProps={
                                                            renderProps
                                                        }
                                                        index={index}
                                                    />
                                                </EndToEndTestIdProvider>
                                            ),
                                        )}{' '}
                                        <AddressInputListItem
                                            renderProps={renderProps}
                                            index={numListItems}
                                            key='extra-option'
                                            enterAddressManually
                                            onItemClick={() => {
                                                setShowDetailedForm(true);
                                                setShowAddressSuggestions(
                                                    false,
                                                );
                                            }}
                                        />
                                        <li
                                            className={getClassName(
                                                styles.item,
                                            )}
                                        >
                                            <img
                                                className={
                                                    styles[
                                                        'google-attribution-image'
                                                    ]
                                                }
                                                src={poweredByGoogle}
                                                alt='Powered by Google - Google Maps API attribution'
                                            />
                                        </li>
                                    </ul>
                                ) : null
                            }
                        </FormikCombobox>
                    </EndToEndTestIdProvider>
                    {addressInputText ? (
                        <div ref={offsetRef} className={styles['form-info']}>
                            {addressInputText}
                        </div>
                    ) : null}
                </span>
            </div>
            <div
                style={
                    showDetailedForm
                        ? { minHeight: minDetailFormHeight }
                        : { display: 'none' }
                }
            >
                <Stack
                    className={getClassName(
                        styles['detail-fields'],
                        showDetailedForm ? styles['detail-fields--show'] : '',
                    )}
                >
                    <div className={styles['fields-row']}>
                        <EndToEndTestIdProvider testId='address-line-2'>
                            <FormikTextInput
                                name='street2'
                                label='Apartment - Unit - Suite (Optional)'
                                errors={errors}
                            />
                        </EndToEndTestIdProvider>
                    </div>
                    <div className={styles['fields-row']}>
                        <EndToEndTestIdProvider testId='address-city'>
                            <FormikTextInput
                                name='city'
                                label='City'
                                errors={errors}
                            />
                        </EndToEndTestIdProvider>
                    </div>
                    <div className={styles['fields-row']}>
                        <EndToEndTestIdProvider testId='address-state'>
                            <FormikSelectInput
                                name='state'
                                label='State'
                                options={stateCodeOptions}
                                includeEmptyOption
                                errors={errors}
                                className={styles['state-input']}
                            />
                        </EndToEndTestIdProvider>
                        <EndToEndTestIdProvider testId='address-zipcode'>
                            <FormikTextInput
                                name='zipcode'
                                label='Zip Code'
                                autoComplete='postal-code'
                                errors={errors}
                                className={styles['zip-input']}
                            />
                        </EndToEndTestIdProvider>
                    </div>
                </Stack>
            </div>
        </Stack>
    );
};

export default AddressInput;
